import type { PlacementBoxBounds } from './PlacementBox.types.ts';
import type {
	BrowserBox,
	CanvasPageInfo,
	PageDimensionsPt,
	PdfBox,
} from './PlacementCanvas.types.ts';

// Stamp geometry is single-sourced in the shared package so preview, resize
// lock, and server validator can't drift. Re-exported here for existing imports.
export {
	STAMP_ASPECT_RATIO,
	STAMP_ASPECT_RATIO_COMPACT,
	STAMP_ASPECT_RATIO_WITNESS,
	MIN_STAMP_WIDTH_PT,
	MIN_STAMP_HEIGHT_PT,
	MIN_STAMP_HEIGHT_COMPACT_PT,
	MIN_STAMP_HEIGHT_WITNESS_PT,
	ASPECT_RATIO_TOLERANCE,
	expectedAspectRatio,
	minHeightFor,
} from '@dealstack/shared/lib/common/business/stampGeometry';

// PDF-space rect (bottom-left origin, points) → browser-space rect
// (top-left origin, CSS pixels at scale).
export const pdfToBrowser = (pdf: PdfBox, pageHeightPt: number, scale: number): BrowserBox => ({
	left: pdf.x * scale,
	top: (pageHeightPt - pdf.y - pdf.height) * scale,
	width: pdf.width * scale,
	height: pdf.height * scale,
});

// Inverse of pdfToBrowser: browser-space rect → PDF-space coords.
export const browserToPdf = (browser: BrowserBox, pageHeightPt: number, scale: number): PdfBox => {
	const widthPt = browser.width / scale;
	const heightPt = browser.height / scale;
	return {
		x: browser.left / scale,
		y: pageHeightPt - browser.top / scale - heightPt,
		width: widthPt,
		height: heightPt,
	};
};

export const clamp = (value: number, min: number, max: number): number =>
	Math.min(Math.max(value, min), max);

// Preview's internal scale tracks box width so fonts/paddings follow it;
// clamped to [0.3, 2] so it doesn't blow up when dragged large at high zoom.
export const computePreviewScale = (boxWidthPx: number): number => clamp(boxWidthPx / 210, 0.3, 2);

/**
 * Box for a finished resize, kept exactly on the slot's locked ratio and inside its page.
 *
 * `react-rnd` maintains `lockAspectRatio` in whole CSS pixels, so the height it writes back is
 * up to ~0.5px off the ratio: a witness box (≈1.183) resized to 114px comes back 96px instead
 * of 96.357, which blows the server's 0.001 ASPECT_RATIO_TOLERANCE and 400s the save. Width is
 * canonical here, as it already is in `defaultBoxFor`, `rescalePlacement` and
 * `anchoredStampSizePt`, so the derived height cannot drift.
 */
export const resizedBoxOnRatio = ({
	width,
	ratio,
	position,
	bounds,
}: {
	width: number;
	ratio: number;
	position: { x: number; y: number };
	bounds: PlacementBoxBounds;
}): BrowserBox => {
	const height = width / ratio;
	return {
		left: clamp(position.x, 0, Math.max(0, bounds.width - width)),
		top: clamp(position.y, 0, Math.max(0, bounds.height - height)),
		width,
		height,
	};
};

/** Nudge a box back inside its page, preserving its size. A witness box rescaled to the
 *  taller ratio grows upward and can overshoot the top edge; saved like that, `fitPlacement`
 *  rejects it at sign time (`y + height > pageHeight`) and dumps the stamp bottom-left of the
 *  last page instead. */
export const clampBoxToPage = (box: PdfBox, page: PageDimensionsPt): PdfBox => ({
	...box,
	x: Math.max(0, Math.min(box.x, page.widthPt - box.width)),
	y: Math.max(0, Math.min(box.y, page.heightPt - box.height)),
});

// Pick the page whose rect contains the drop centre (falls back to the source
// page in inter-page gaps), clamp inside it, convert to PDF coords. Pure (no
// DOM) so it's unit-testable — caller passes in page rects + dims.
export const resolveDropTarget = (args: {
	centre: { x: number; y: number };
	sourcePageNum: number;
	stampWidthPx: number;
	stampHeightPx: number;
	scale: number;
	pages: CanvasPageInfo[];
}): { pageNum: number; pdfBox: PdfBox } | null => {
	const { centre, sourcePageNum, stampWidthPx, stampHeightPx, scale, pages } = args;

	const containing = pages.find(
		(p) =>
			centre.x >= p.rect.left &&
			centre.x <= p.rect.right &&
			centre.y >= p.rect.top &&
			centre.y <= p.rect.bottom
	);
	const target = containing ?? pages.find((p) => p.pageNum === sourcePageNum) ?? null;
	if (!target) {
		return null;
	}

	const rawLeft = centre.x - target.rect.left - stampWidthPx / 2;
	const rawTop = centre.y - target.rect.top - stampHeightPx / 2;
	const localLeft = Math.max(0, Math.min(target.dims.widthPt * scale - stampWidthPx, rawLeft));
	const localTop = Math.max(0, Math.min(target.dims.heightPt * scale - stampHeightPx, rawTop));

	return {
		pageNum: target.pageNum,
		pdfBox: browserToPdf(
			{ left: localLeft, top: localTop, width: stampWidthPx, height: stampHeightPx },
			target.dims.heightPt,
			scale
		),
	};
};

// Date boxes have no signing role, so they can't use signatureSlotKey's tuple; the prefix keeps
// the two key spaces disjoint in the shared placements Map.
export const dateKey = (placementIndex: number): string => `date:${placementIndex}`;

/** The placementIndex encoded in a date key, or null when the key is not a date key. */
export const parseDateKey = (key: string): number | null => {
	if (!key.startsWith('date:')) {
		return null;
	}
	const parsed = Number.parseInt(key.slice('date:'.length), 10);
	return Number.isInteger(parsed) ? parsed : null;
};
